import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Box, Link, Typography } from '@mui/material'
import { DynamicFlowNode, useDynamicFlowController } from 'components/common/DynamicFlow'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { PurchaseFlowStepKey, usePurchaseFlowUtilities } from './contexts/PurchaseFlowUtilities.context'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { createSearchParams, useLocation } from 'react-router-dom'
import { usePurchaseFlowConfig, usePurchaseFlowOrderMeta, useTargetOrderUser } from './contexts'
import { usePurchaseFlowProducts } from './contexts/PurchaseFlowProducts.context'

import { EmbedCoBranding } from 'components/common/Embed/EmbedCoBranding.component'
import { MUIButton } from 'components/common/MUIButton'
import { OrderAssignmentStatusFilter } from 'constants/misc'
import { Path } from 'constants/router'
import Stack from '@mui/material/Stack'
import { getPropertyAddressFromGooglePlace } from 'utils/location/googlePlaceUtils'
import { getTermsLink } from 'utils/localization'
import i18n from 'translations/i18n'
import { useAddressMap } from 'components/common/AdressMap'
import { useAuth0 } from 'utils/auth'
import { useEmbedConfig } from './contexts/EmbedConfig.context'
import { useEmbedConsent } from './contexts/EmbedConsent.context'
import { useFloorPlanCertification } from '../steps/FloorPlanCertification/_main'
import { useFloorPlanConfig } from '../steps/FloorPlanConfig/_main/FloorPlanConfigStep.context'
import { useMeasurements } from '../steps/MeasurementStep/MeasurementStep.context'
import { usePatchPropertyDetail } from 'dataQueries'
import { usePropertyInformationStep } from '../steps/PropertyInformationStep/PropertyInformationStep.context'
import { usePurchaseFlowPaymentStatus, usePurchaseFlowPlaceOrder } from '../steps/Validation/_main/contexts'
import { usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'
import { useNextStepValidationErrors } from './hooks/useNextStepValidationErrors'
import { PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE } from 'utils/analytics/screenEntryLayout'
import { ProductKind } from 'constants/product'
import { useUserData } from 'components/contexts/UserDataContext'
import { isOrdererTheContactPerson } from '../common/InstructionListing/instructionCards/MeetingInstructionsCard/contactPersonTarget'

/**
 * @component Footer component for Purchase Flow containing navigation buttons
 * @example <PurchaseFlowFooter />
 */
export const PurchaseFlowFooter: React.FC = () => {
  const { t } = useTranslation(['actions', 'order'])
  const location = useLocation()
  const exitPurchaseFlowRedirectUrl = location.state?.redirectedFrom

  const { roles } = useAuth0()
  const { isEmbed } = useEmbedConfig()
  const { isConsentRequired, isConsentBlockingOrder } = useEmbedConsent()
  const { exitPurchaseFlow } = usePurchaseFlowUtilities()
  const { isOrderOrStripeInProgress, onPlaceOrder, orderHasBeenPlaced, orderIsComplete } = usePurchaseFlowPlaceOrder()
  const { stripeError } = usePurchaseFlowPaymentStatus()
  const { isStripePaymentMethod, ccEmails, selectedMeetingType, meetingInfoFillInCheckedMap } = usePurchaseFlowOrderMeta()
  const { targetUser } = useTargetOrderUser()
  const { baseUserData } = useUserData()
  const { selectedCategory } = usePurchaseFlowConfig()
  const { selectedSegment } = usePurchaseFlowProducts()
  const { getSelectedOptionsAnalytics } = useFloorPlanConfig()
  const { logMeasurementRequirements } = useMeasurements()
  const { logFloorPlanCertificationRequirements } = useFloorPlanCertification()
  const {
    isUpdatePropertyAddressFlow,
    realEstatePropertyId,
    userPropertyWorkspaceId,
    propertyName,
    selectedAssignmentPlace,
    isAddressManually,
    addressManual,
    streetNumberManual,
    cityManual,
    zipCodeManual,
    countryManual,
    getPropertyQuery,
  } = usePurchaseFlowRealEstateProperty()
  const { markerPosition } = useAddressMap()
  const { resetLandRegisterSheetItems } = usePropertyInformationStep()

  const flowCtrl = useDynamicFlowController()
  const getNextStepValidationErrors = useNextStepValidationErrors()

  const updatePropertyDetail = usePatchPropertyDetail()

  // Local loading state to prevent double clicks before the mutation for last step send order or stripe payment starts
  const [isSendOrderLoading, setIsSendOrderLoading] = useState(false)

  const isLastStep = useMemo(() => !flowCtrl.currentNode?.next, [flowCtrl.currentNode?.next])
  // The order exists but its card / SEPA payment was refused: the last step's click retries the payment on the same order.
  const canRetryPayment = orderHasBeenPlaced && isStripePaymentMethod && !!stripeError
  const isFirstStep = useMemo(() => !flowCtrl.currentNode?.prev, [flowCtrl.currentNode?.prev])

  const termsLink = useMemo(() => getTermsLink(i18n.language), [])
  const isLoading = useMemo(() =>
    isLastStep ? (isOrderOrStripeInProgress || isSendOrderLoading) : false
    , [isLastStep, isOrderOrStripeInProgress, isSendOrderLoading])

  // What the broker left on the billing and instructions screens, on the cleared step's click (ISSUE-4023): the
  // enters-screen events only know the initial state. The on-site checkbox exists for two meeting types and targets
  // the orderer, unless an admin orders for a client; null when the chosen meeting type has no checkbox.
  const stepLeaveAnalytics = useCallback((stepKey: string) => {
    if (stepKey === PurchaseFlowStepKey.BILLING) return { cc_emails_added_count: ccEmails.size }
    if (stepKey !== PurchaseFlowStepKey.INSTRUCTIONS) return {}

    const checkboxMeetingType = selectedMeetingType === ProductKind.MEETING_ON_SITE || selectedMeetingType === ProductKind.KEYS_PICKUP
      ? selectedMeetingType
      : null
    const isOrdererTheContact = isOrdererTheContactPerson(targetUser.email, baseUserData?.email)
    return { on_site_contact_is_orderer: checkboxMeetingType ? isOrdererTheContact && meetingInfoFillInCheckedMap[checkboxMeetingType] : null }
  }, [ccEmails, selectedMeetingType, meetingInfoFillInCheckedMap, targetUser.email, baseUserData?.email])

  const onNextAnalytics = useCallback((currentStep: DynamicFlowNode) => {
    // Analytics for the click next; a click on the disabled button is logged by onBlockedNextClick (ISSUE-3955)
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_CLICK_NEXT_STEP, {
      CurrentStep: currentStep.key,
      NewStep: currentStep.next,
      validation_passed: true,
      validation_errors: [],
      ...stepLeaveAnalytics(currentStep.key),
    })

    // Analytics for the Floor Plan config step
    if (currentStep.key === PurchaseFlowStepKey.FLOOR_PLAN_CONFIG) {
      const options = getSelectedOptionsAnalytics()
      logAnalyticsEvent(AnalyticsEvent.FLOOR_PLAN_OPTIONS_SELECTED, { options, category: selectedCategory })
    }

    // Analytics for the Floor Plan certification step aka Living area calculation
    if (currentStep.key === PurchaseFlowStepKey.FLOOR_PLAN_CERTIFICATION) {
      logFloorPlanCertificationRequirements()
    }

    // Analytics for the measurement step
    if (currentStep.key === PurchaseFlowStepKey.MEASUREMENT_REQUIREMENTS) {
      logMeasurementRequirements()
    }
  }, [getSelectedOptionsAnalytics, logFloorPlanCertificationRequirements, logMeasurementRequirements, selectedCategory, stepLeaveAnalytics])

  const onNextClick = useCallback(() => {
    if (isLastStep) {
      // Prevent multiple clicks when order is already being placed
      if (isOrderOrStripeInProgress || isSendOrderLoading || (orderHasBeenPlaced && !canRetryPayment)) {
        return
      }
      // The last step's next-step click, which the early return below would otherwise skip (ISSUE-3932); a payment retry is not a new order click
      if (flowCtrl.currentNode && !canRetryPayment) onNextAnalytics(flowCtrl.currentNode)
      // Set local loading state immediately to prevent double clicks. onPlaceOrder is async (it may
      // record the embed consent first): clear the local state on settle so a failed consent POST
      // doesn't leave the CTA stuck in loading — the mutation's own pending state takes over when
      // the order placement actually starts.
      setIsSendOrderLoading(true)
      void Promise.resolve(onPlaceOrder()).finally(() => setIsSendOrderLoading(false))
      return
    }

    const currentStep = flowCtrl.currentNode
    if (!currentStep?.next) return

    if (currentStep.key === PurchaseFlowStepKey.ADDRESS && isUpdatePropertyAddressFlow && realEstatePropertyId) {

      const manualAddress = {
        street: addressManual,
        streetNumber: streetNumberManual,
        city: cityManual,
        postalCode: zipCodeManual,
        country: countryManual,
        regionCode: '', // Not provided in manual address
        coordinates: {
          lat: markerPosition?.lat ?? 0,
          lng: markerPosition?.lng ?? 0
        }
      }

      const searchedAddress = selectedAssignmentPlace ? getPropertyAddressFromGooglePlace(selectedAssignmentPlace) : null

      const payload = {
        title: propertyName,
        address: isAddressManually ? manualAddress : searchedAddress
      }

      updatePropertyDetail.mutate(
        { propertyId: realEstatePropertyId, workspaceId: userPropertyWorkspaceId, payload }
      )
    }
    onNextAnalytics(currentStep)

    flowCtrl.setHead(currentStep.next)

    // Omit flowCtrl dependency to prevent loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowCtrl.currentNode?.next, isLastStep, onPlaceOrder, isOrderOrStripeInProgress, orderHasBeenPlaced, canRetryPayment, isSendOrderLoading])

  const isNextDisabled = flowCtrl.head ? !flowCtrl.validityMap.get(flowCtrl.head) : false || isLoading

  // ISSUE-3955: where brokers get stuck. A click while the order is being placed is a wait, not a missing field.
  const onBlockedNextClick = () => {
    const currentStep = flowCtrl.currentNode
    if (!isNextDisabled || isLoading || !currentStep) return

    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_CLICK_NEXT_STEP, {
      CurrentStep: currentStep.key,
      NewStep: currentStep.next,
      validation_passed: false,
      validation_errors: getNextStepValidationErrors(currentStep.key),
    })
  }

  const onBackClick = useCallback(() => {
    const currentStep = flowCtrl.currentNode

    if (!currentStep?.prev) return

    if (currentStep.key === PurchaseFlowStepKey.PROPERTY_INFORMATION) {
      // Reset the land register sheet items when going back from Property Information step
      resetLandRegisterSheetItems()
    }

    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_CLICK_PREVIOUS_STEP, {
      CurrentStep: currentStep.key,
      NewStep: currentStep.prev
    })

    flowCtrl.setHead(currentStep.prev)

    // Omit flowCtrl dependency to prevent loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowCtrl.currentNode?.prev, flowCtrl.head])

  useEffect(() => {
    // Go back to Order Dashboard or Property Dashboard after getting the order status message.
    // Never in embed (ISSUE-2377): the iframe must not land on the standalone dashboard —
    // the Validation step shows a terminal confirmation screen instead.
    if (orderHasBeenPlaced && !isStripePaymentMethod && !isEmbed) {
      // If the flow is a property path, redirect to the property page where we came from
      if (!!exitPurchaseFlowRedirectUrl) {
        exitPurchaseFlow(
          false,
          {
            pathname: exitPurchaseFlowRedirectUrl,
          }
        )
        // If the flow is not a property path, redirect to property path if FF allow and isClient, otherwise redirect the Order Dashboard with search params
      } else {
        exitPurchaseFlow(
          false,
          {
            pathname: Path.INDEX,
            search: (createSearchParams({ status: OrderAssignmentStatusFilter.ONGOING }).toString()),
          }
        )
      }

    }
  }, [exitPurchaseFlow, exitPurchaseFlowRedirectUrl, isEmbed, isStripePaymentMethod, orderHasBeenPlaced, roles.isClient])

  // Reset local loading state when the actual loading state changes or order is placed
  useEffect(() => {
    if (isOrderOrStripeInProgress || orderHasBeenPlaced) {
      setIsSendOrderLoading(false)
    }
  }, [isOrderOrStripeInProgress, orderHasBeenPlaced])

  // Terminal embed state (ISSUE-2377): the confirmation screen is shown and there is nothing left
  // to navigate — hide the whole footer. Until then a refused card keeps its Retry payment CTA.
  if (isEmbed && orderIsComplete) return null

  // Embed chrome-less product-step states (ISSUE-3080): the area gate carries its own commit button
  // (this footer's Next would sit beneath it permanently disabled), and the property-error screen is
  // terminal (an enabled Next would navigate straight off an error page). Mirrors the cart guard in
  // PurchaseFlow.controller.
  if (isEmbed && flowCtrl.currentNode?.key === PurchaseFlowStepKey.PRODUCT &&
    (!selectedSegment || getPropertyQuery.isError)) return null

  return (
    <Stack
      gap={2}
      height="100%"
      marginLeft={4}
      marginRight={4}
      alignItems="center"
      flexDirection="row"
      justifyContent="flex-end"
    >

      {/* The auto margin keeps the co-branding on the far left while everything else stays
          right-aligned exactly as in the non-embed flow. */}
      {isEmbed &&
        <Box marginRight="auto">
          <EmbedCoBranding />
        </Box>
      }

      {/* Terms & Conditions for the last step. Hidden while the embed consent checkbox is shown
          in the summary (ISSUE-2017) — consent must not be stated twice. */}
      {isLastStep && !(isEmbed && isConsentRequired) &&
        <Stack>
          <Typography variant='text-sm' color={GRAY_700}>
            <Trans t={t} i18nKey="order:step_validation:agree_tos">
              <Link
                href={termsLink}
                rel="noopener noreferrer"
                target="_blank"
                sx={{ color: GRAY_900, textDecorationColor: GRAY_900 }}
              />
            </Trans>
          </Typography>
        </Stack>
      }

      {/* On the embed's last step the consent checkbox sits below the summary (ISSUE-3081) and can be
          below the fold in a short iframe — without this hint the disabled CTA gives no feedback. */}
      {!isLoading &&
        ((!isLastStep && flowCtrl.head && !flowCtrl.validityMap.get(flowCtrl.head)) ||
          (isLastStep && isEmbed && isConsentBlockingOrder)) &&
        <Typography variant='text-sm' color={GRAY_700}>
          {t('order:step_validation:fill_required_fields')}
        </Typography>
      }

      {!isFirstStep &&
        // Going back after placement would request the payment intent again, which the backend refuses for a placed order
        <MUIButton type='secondaryBorder' variant='outlined' onClick={onBackClick} disabled={isLoading || orderHasBeenPlaced}>
          {t('Previous')}
        </MUIButton>
      }

      {/* A disabled button gets no clicks (pointer-events: none), so this wrapper catches the ones on the greyed-out
          Next for Clicks_next_step_purchase_flow with validation_passed: false. */}
      <Box component="span" display="inline-flex" onClick={onBlockedNextClick} data-testid="purchase-flow-next-wrapper" {...{ [PURCHASE_FLOW_NEXT_CTA_ATTRIBUTE]: '' }}>
        <MUIButton
          onClick={onNextClick}
          disabled={isNextDisabled}
          isLoading={isLoading}
        >
          {!isLastStep ? t('Next') : canRetryPayment ? t('order:step_validation:retry_payment') : t('order:step_validation:agree_and_order')}
        </MUIButton>
      </Box>

    </Stack>
  )
}
